import React from 'react';
import Svg, { Circle, Path } from 'react-native-svg';

/**
 * Реакция, нарисованная линией (заказ владельца 2026-09-09).
 *
 * <p><b>Зачем не эмодзи.</b> Весь интерфейс списка чатов — тонкая штриховая графика в один
 * тон: колокольчик, молния, кнопки шапки, узор на панели. Цветная эмодзи рядом с ними
 * читается как приклеенный стикер, а обесцвеченная фильтром — как выцветший стикер: у неё
 * остаются тени, градиенты и толстые заливки, то есть чужая пластика. Поэтому иконки
 * рисуются теми же средствами, что и остальные: {@code stroke} по прозрачному фону, без
 * заливки, скруглённые концы.
 *
 * <p><b>Почему набор конечный и этого достаточно.</b> Поставить можно только те реакции,
 * что отдаёт панель выбора, — {@code EMOJI_POPULAR.slice(0, 8)} в {@code ChatScreen}.
 * Восемь штук, и все восемь нарисованы здесь. На всё прочее (реакция из старых данных,
 * расширенный набор в будущем) компонент возвращает {@code null}, и вызывающий сам решает,
 * чем закрыть дыру, — молча рисовать не тот значок хуже, чем показать эмодзи.
 *
 * <p><b>Об упрощениях.</b> На 20 dp лицо живёт не деталями, а силуэтом, поэтому черты
 * сведены к минимуму: точка-глаз, дуга-рот. У «🥰» вместо плавающих сердечек — сердечки
 * вместо глаз: в оригинале они мелкие и по краям, на такой кегле превратились бы в
 * неразличимые крапины, а глаз-сердце читается сразу. Спутать не с чем: «😍» в наборе нет.
 */
const STROKE_WIDTH = 1.8;

/** Точка (глаз): отрезок нулевой длины со скруглённым концом. */
function Dot({ x, y, color }: { x: number; y: number; color: string }) {
  return (
    <Path
      d={`M${x} ${y}h.01`}
      stroke={color}
      strokeWidth={2.4}
      strokeLinecap="round"
    />
  );
}

function stroke(color: string) {
  return {
    stroke: color,
    strokeWidth: STROKE_WIDTH,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    fill: 'none' as const,
  };
}

/** Общий кружок лица — у всех четырёх одинаковый, чтобы они читались одной семьёй. */
function FaceCircle({ color }: { color: string }) {
  return <Circle cx={12} cy={12} r={8.6} {...stroke(color)} />;
}

const SMILE = 'M8.2 14c.9 1.3 2.2 2 3.8 2s2.9-.7 3.8-2';

function glyphFor(emoji: string, color: string): React.ReactNode | null {
  const s = stroke(color);
  switch (emoji) {
    // 😀 — ровная улыбка и точки-глаза: базовое лицо, от которого отличаются остальные три.
    case '\u{1F600}':
      return (
        <>
          <FaceCircle color={color} />
          <Path d={SMILE} {...s} />
          <Dot x={9.2} y={10} color={color} />
          <Dot x={14.8} y={10} color={color} />
        </>
      );
    // 😂 — глаза зажмурены дугами вверх, рот шире, по бокам слёзы.
    case '\u{1F602}':
      return (
        <>
          <FaceCircle color={color} />
          <Path d="M7.9 13.4c1 1.7 2.4 2.7 4.1 2.7s3.1-1 4.1-2.7" {...s} />
          <Path d="M7.9 10.6c.6-.9 1.7-.9 2.3 0" {...s} />
          <Path d="M13.8 10.6c.6-.9 1.7-.9 2.3 0" {...s} />
          <Path d="M5.1 12.6c-.7 1.1-1 2-1 2.7" {...s} />
          <Path d="M18.9 12.6c.7 1.1 1 2 1 2.7" {...s} />
        </>
      );
    // 🥰 — сердечки вместо глаз (см. «Об упрощениях» в шапке файла).
    case '\u{1F970}':
      return (
        <>
          <FaceCircle color={color} />
          <Path d={SMILE} {...s} />
          <Path d="M9.2 11.1 7.9 9.9a.9.9 0 0 1 1.3-1.2.9.9 0 0 1 1.3 1.2Z" {...s} />
          <Path d="M14.8 11.1l-1.3-1.2a.9.9 0 0 1 1.3-1.2.9.9 0 0 1 1.3 1.2Z" {...s} />
        </>
      );
    // 😉 — один глаз точкой, другой прикрыт дугой: подмигивание держится на асимметрии.
    case '\u{1F609}':
      return (
        <>
          <FaceCircle color={color} />
          <Path d={SMILE} {...s} />
          <Path d="M8.1 10.5c.6-.8 1.7-.8 2.3 0" {...s} />
          <Dot x={14.8} y={10} color={color} />
        </>
      );
    // 👍 / 👎 — рука и манжет: тот же силуэт, перевёрнутый.
    case '\u{1F44D}':
      return (
        <>
          <Path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3Z" {...s} />
          <Path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" {...s} />
        </>
      );
    case '\u{1F44E}':
      return (
        <>
          <Path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3Z" {...s} />
          <Path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" {...s} />
        </>
      );
    case '\u{2764}\u{FE0F}':
    case '\u{2764}':
      return (
        <Path
          d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z"
          {...s}
        />
      );
    // 🔥 — внешний язык пламени и внутренний: без второго силуэт читается как капля.
    case '\u{1F525}':
      return (
        <>
          <Path d="M12 22a6 6 0 0 0 6-6c0-4.5-4.5-8-6-12-1.5 4-6 7.5-6 12a6 6 0 0 0 6 6Z" {...s} />
          <Path d="M12 22a2.8 2.8 0 0 0 2.8-2.8c0-2-1.9-3.3-2.8-5.2-.9 1.9-2.8 3.2-2.8 5.2A2.8 2.8 0 0 0 12 22Z" {...s} />
        </>
      );
    default:
      return null;
  }
}

export function ReactionGlyph({
  emoji,
  size,
  color,
}: {
  emoji: string;
  size: number;
  color: string;
}) {
  const glyph = glyphFor(emoji, color);
  if (!glyph) return null;
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      {glyph}
    </Svg>
  );
}

/** Есть ли для этой реакции нарисованная иконка — чтобы вызывающий выбрал запасной путь. */
export function hasReactionGlyph(emoji: string): boolean {
  return glyphFor(emoji, '#000') !== null;
}
